import { useEffect, useMemo, useRef, useState } from 'react';
import { getLevelCriteria } from '../services/jrMaklerDetail.js';
import { listMaklers, getLevelDetail, assignLevel, suggestLevel } from '../services/maklerLevels.js';
import { usePolicy, levelFor } from '../components/PolicyContext.jsx';

// Zařazení do úrovně (level assignment) — JR/JCH. Lists the maklers the current
// user manages (via /maklerOverview), then for each shows the effective level,
// Drill's pending suggestion (if any) and the audit history. The JR (or JCH)
// can write a new assignment or store a suggestion without committing it.
//
// Auth is on the backend: /maklerOverview is JR-only, /makler/:id/level is
// JR + JCH. Anyone without a role here should not be on this route.

const EMPTY_FORM = {
  maklerId: null,
  levelName: '',
  fromDate: new Date().toISOString().slice(0, 10),
  reason: '',
};

export default function LevelAssignment() {
  const { policy } = usePolicy();
  const levels = policy.levels.filter((l) => l.numeric > 0);
  const [maklers, setMaklers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null); // full detail for the selected makler
  const [criteria, setCriteria] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const requestId = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    setStatusMsg('');
    try {
      const list = await listMaklers();
      setMaklers(list);
      if (list.length && !selectedId) {
        setSelectedId(list[0].id);
      }
    } catch (err) {
      setError(err.message || 'Makléře se nepodařilo načíst.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedMakler = useMemo(
    () => maklers.find((m) => m.id === selectedId) || null,
    [maklers, selectedId]
  );

  const detail = useMemo(
    () => (selectedMakler && selected?.makler_id === selectedMakler.id ? selected : null),
    [selectedMakler, selected]
  );

  const openMakler = async (id) => {
    const request = ++requestId.current;
    setSelected(null);
    setCriteria(null);
    setDetailLoading(true);
    setError('');
    setStatusMsg('');
    try {
      const [data, actualCriteria] = await Promise.all([
        getLevelDetail(id),
        getLevelCriteria(id),
      ]);
      if (request !== requestId.current) return;
      setSelected(data);
      setCriteria(actualCriteria);
      setForm({ ...EMPTY_FORM, maklerId: id });
    } catch (err) {
      if (request === requestId.current) {
        setError(err.response?.data?.error || err.message || 'Podklady pro zařazení se nepodařilo načíst.');
      }
    } finally {
      if (request === requestId.current) setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (selectedId) {
      openMakler(selectedId);
    }
    return () => { requestId.current++; };
  }, [selectedId]);

  const updateForm = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.levelName) return;
    setSaving(true);
    setError('');
    setStatusMsg('');
    try {
      await assignLevel(form.maklerId, {
        levelName: form.levelName,
        levelNumeric: levels.find((l) => l.name === form.levelName)?.numeric,
        fromDate: form.fromDate,
        reason: form.reason,
        reasonType: 'jr',
      });
      setStatusMsg('Zápis uložen.');
      setForm({ ...EMPTY_FORM, maklerId: form.maklerId, fromDate: new Date().toISOString().slice(0, 10) });
      // refresh the list + detail
      if (selectedId) await openMakler(selectedId);
      setMaklers(await listMaklers());
    } catch (err) {
      setError(err.message || 'Zápis se nepodařilo uložit.');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggest = async () => {
    if (!form.levelName) return;
    setSaving(true);
    setError('');
    setStatusMsg('');
    try {
      await suggestLevel(form.maklerId, {
        levelName: form.levelName,
        levelNumeric: levels.find((l) => l.name === form.levelName)?.numeric,
        fromDate: form.fromDate,
      });
      setStatusMsg('Návrh Drillu uložen do historie.');
      setForm({ ...EMPTY_FORM, maklerId: form.maklerId, fromDate: new Date().toISOString().slice(0, 10) });
      if (selectedId) await openMakler(selectedId);
    } catch (err) {
      setError(err.message || 'Návrh se nepodařilo uložit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Zařazení do úrovně</h1>
          <p className="dashboard__lead">
            Jednou měsíčně po 20., kdy jsou hotová čísla za minulý měsíc. Drill
            navrhuje, zapisuje JR, JCH může zápis změnit – při neshodě rozhoduje JCH. Kritéria jsou
            orientační. Než začneš, porovnej nábory a provize v Drillu s Maxisem.
          </p>
        </div>
      </div>

      {error && (
        <p className="dashboard__error" role="alert">
          {error}
        </p>
      )}
      {statusMsg && (
        <p className="dashboard__status" role="status">
          {statusMsg}
        </p>
      )}

      {loading ? (
        <p className="dashboard__status">Načítá se…</p>
      ) : maklers.length === 0 ? (
        <p className="dashboard__status">Zatím žádný makléř.</p>
      ) : (
        <div className="level-assign">
          {/* Makler picker */}
          <article className="card card--aside">
            <h2 className="card__title">Makléř</h2>
            <ul className="level-assign__list">
              {maklers.map((m) => (
                <li
                  key={m.id}
                  className={`level-assign__item${m.id === selectedId ? ' is-selected' : ''}`}
                  onClick={() => setSelectedId(m.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setSelectedId(m.id);
                  }}
                >
                  <span className="level-assign__name">{m.name || m.username}</span>
                  <span className="level-assign__level">
                    {m.level_name ? (
                      <span className="level-chip" style={{ color: levelFor(policy, m)?.color }}>
                        {levelFor(policy, m)?.name ?? m.level_name}
                      </span>
                    ) : (
                      <span className="level-assign__none">—</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </article>

          {/* Detail + write form for the selected makler */}
          <article className="card">
            <h2 className="card__title">
              {selectedMakler?.name || selectedMakler?.username || 'Zařazení'}
            </h2>
            <button className="button button--secondary" type="button"
              disabled={!selectedId || detailLoading || saving}
              onClick={() => openMakler(selectedId)}>
              Obnovit podklady
            </button>

            {detailLoading ? (
              <p className="dashboard__status">Načítají se skutečné podklady…</p>
            ) : detail ? (
              <>
                {criteria && (
                  <div className="level-assign__criteria">
                    <h3 className="level-assign__h3">Podklady pro zařazení</h3>
                    <p className="card__hint">
                      Plnění posledních 6 uzavřených týdnů a nábory vycházejí z uložených
                      aktivit. Provize vycházejí z importovaných záznamů do dnešního dne.
                    </p>
                    <div className="level-assign__sem">
                      {criteria.weeks.length === 0 ? (
                        <span className="card__hint">Zatím žádný uzavřený týden.</span>
                      ) : (
                        criteria.weeks.map((w) => {
                          const cls = { green: 'semafor--grn', orange: 'semafor--org', red: 'semafor--red' }[w.color] || '';
                          return (
                            <span key={w.start} className={`semafor ${cls}`} title={w.start}>
                              {w.pct == null ? '–' : `${Math.round(w.pct)} %`}
                            </span>
                          );
                        })
                      )}
                    </div>
                    <ul className="card__text">
                      <li>Týdnů v červené (posledních 6): {criteria.weeksInRed}</li>
                      <li>Nábory za 3 měsíce: {criteria.acquisitions3m}</li>
                      <li>Provize za 3 měsíce: {Math.round(criteria.commission3m).toLocaleString('cs-CZ')} Kč</li>
                      <li>Provize za 12 měsíců: {Math.round(criteria.commission12m).toLocaleString('cs-CZ')} Kč</li>
                    </ul>
                    <p className="card__text">
                      Doporučení podle nastavených kritérií:{' '}
                      {criteria.recommendation?.name || 'Žádná úroveň nesplňuje kritéria, kritéria nejsou nastavena nebo chybí šest hodnocených týdnů.'}
                    </p>
                    {criteria.recommendation && (
                      <button className="button button--secondary" type="button" disabled={saving}
                        onClick={() => setForm((f) => ({ ...f, levelName: criteria.recommendation.name }))}>
                        Vybrat doporučenou úroveň
                      </button>
                    )}
                  </div>
                )}

                {/* Drill's suggestion */}
                {detail.suggestion && (
                  <div className="level-assign__suggestion">
                    <strong>Návrh Drillu:</strong>{' '}
                    <span className="level-chip" style={{ color: levelFor(policy, detail.suggestion)?.color }}>
                      {levelFor(policy, detail.suggestion)?.name ?? detail.suggestion.level_name}
                    </span>
                    {detail.suggestion.from_date && (
                      <span>od {new Date(detail.suggestion.from_date).toLocaleDateString('cs-CZ')}</span>
                    )}
                  </div>
                )}

                {/* Audit history */}
                {detail.history.length > 0 && (
                  <div className="level-assign__history">
                    <h3 className="level-assign__h3">Historie zařazení</h3>
                    <ol className="level-assign__history-list">
                      {detail.history.slice().reverse().map((h) => (
                        <li key={h.id} className="level-assign__history-item">
                          <span className="level-assign__history-date">
                            {h.from_date
                              ? new Date(h.from_date).toLocaleDateString('cs-CZ')
                              : '—'}
                          </span>
                          <span className="level-assign__history-text">
                            {h.level_name} — {h.jr_reason || 'bez zdůvodnění'}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <p className="card__hint">
                  Úroveň 4 · {policy.levels[4].name}. Návrh se uloží do
                  historie vedle tvého rozhodnutí, i když rozhodneš jinak. Záznamy se nemažou, oprava
                  je nový záznam.
                </p>

                {/* Write form */}
                <div className="level-assign__form">
                  <label className="auth__label">
                    Úroveň
                    <select className="auth__input" value={form.levelName} onChange={updateForm('levelName')}>
                      <option value="">— vybrat úroveň —</option>
                      {levels.map((l) => (
                        <option key={l.numeric} value={l.name}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="auth__label">
                    Datum platnosti
                    <input
                      className="auth__input"
                      type="date"
                      value={form.fromDate}
                      onChange={updateForm('fromDate')}
                    />
                  </label>

                  <label className="auth__label">
                    Zdůvodnění (nepovinné)
                    <input
                      className="auth__input"
                      type="text"
                      value={form.reason}
                      onChange={updateForm('reason')}
                      placeholder="Např. splněno kritérium náborů"
                    />
                  </label>

                  <div className="level-assign__actions">
                    <button
                      className="button button--spot"
                      onClick={handleSubmit}
                      disabled={!form.levelName || saving}
                    >
                      Uložit zápis
                    </button>
                    <button
                      className="button button--quiet"
                      onClick={handleSuggest}
                      disabled={!form.levelName || saving}
                    >
                      Uložit návrh Drillu
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="dashboard__status">Vyberte makléře pro zobrazení úrovně.</p>
            )}
          </article>
        </div>
      )}
    </section>
  );
}
